import React, { useCallback, useEffect, useState } from "react";
import { apiGet, getApiUrl } from "../../utils/apiClient";

type OcrDebugRow = {
  timestamp: number;
  game_clock: number | null;
  clock_confidence: number;
  warnings: string[];
  has_binary: boolean;
  has_crop: boolean;
  has_annotated: boolean;
  has_minimap: boolean;
};

type ImageType = "binary" | "crop" | "annotated" | "minimap";

const IMAGE_TYPE_LABELS: Record<ImageType, string> = {
  binary: "Binary (Tesseract input)",
  crop: "Gray crop (upscaled)",
  annotated: "Annotated frame",
  minimap: "Minimap crop",
};

function fmtSec(totalSec: number): string {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const PAGE_SIZE = 20;

type Props = { clipId: string | null };

const MacroOcrDebugViewer: React.FC<Props> = ({ clipId }) => {
  const [rows, setRows] = useState<OcrDebugRow[]>([]);
  const [allWarnings, setAllWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const [page, setPage] = useState(0);
  const [imgType, setImgType] = useState<ImageType>("binary");

  const load = useCallback(async () => {
    if (!clipId) return;
    setError(null);
    setMissing(false);
    setRows([]);
    try {
      const data = await apiGet(`/api/macro/ocr-debug?clipId=${encodeURIComponent(clipId)}`);
      if (data.missing) {
        setMissing(true);
        return;
      }
      setRows((data.rows as OcrDebugRow[]) ?? []);
      setAllWarnings((data.allWarnings as string[]) ?? []);
      setPage(0);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load OCR debug data.");
    }
  }, [clipId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!clipId) {
    return (
      <section className="macro-ocr-debug">
        <h2>OCR debug viewer</h2>
        <p className="macro-muted">Select a clip to inspect its OCR debug images.</p>
      </section>
    );
  }

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const hasTesseractError = allWarnings.includes("pytesseract_unavailable_install_tesseract");

  return (
    <section className="macro-ocr-debug">
      <h2>OCR debug viewer</h2>
      <p className="macro-muted">
        Debug images are written sparsely during ingest (default: every 10 s starting at 8:00). Each entry shows what
        Tesseract actually received and the clock value that was parsed (or — if OCR failed).
      </p>

      {hasTesseractError && (
        <div className="macro-ocr-debug-warn">
          <strong>Tesseract / pytesseract not installed</strong> — OCR cannot run on this machine. Install Tesseract OCR
          and the <code>pytesseract</code> Python package, then re-ingest to get real clock readings.
          <br />
          <span className="macro-muted">
            Windows: <code>winget install UB-Mannheim.TesseractOCR</code>, then{" "}
            <code>pip install pytesseract</code>. Ensure <code>tesseract.exe</code> is on PATH or set{" "}
            <code>TESSERACT_CMD</code>.
          </span>
        </div>
      )}

      {error && <div className="macro-error">{error}</div>}

      {missing && !error && (
        <p className="macro-muted">
          No vision debug file found for this clip. Re-ingest the clip to generate debug images (they are written
          starting at 8:00 video time by default).
        </p>
      )}

      {rows.length > 0 && (
        <>
          <div className="macro-ocr-debug-controls">
            <span>{rows.length} debug frame(s)</span>
            <label>
              Show:{" "}
              <select value={imgType} onChange={(e) => setImgType(e.target.value as ImageType)}>
                {(Object.keys(IMAGE_TYPE_LABELS) as ImageType[]).map((t) => (
                  <option key={t} value={t}>
                    {IMAGE_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              ← Prev
            </button>
            <span>
              Page {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            >
              Next →
            </button>
          </div>

          <div className="macro-ocr-debug-grid">
            {pageRows.map((row) => {
              const hasImg =
                (imgType === "binary" && row.has_binary) ||
                (imgType === "crop" && row.has_crop) ||
                (imgType === "annotated" && row.has_annotated) ||
                (imgType === "minimap" && row.has_minimap);

              const imgUrl = hasImg
                ? `${getApiUrl("/api/macro/ocr-debug-image")}?clipId=${encodeURIComponent(clipId)}&ts=${row.timestamp}&type=${imgType}&cb=${row.timestamp}`
                : null;

              const gameTime =
                row.game_clock !== null && row.clock_confidence > 0
                  ? fmtSec(row.game_clock)
                  : "—";

              const confLabel =
                row.clock_confidence > 0 ? row.clock_confidence.toFixed(2) : "—";

              const isBadConf = row.clock_confidence <= 0;

              return (
                <div key={row.timestamp} className={`macro-ocr-debug-card${isBadConf ? " macro-ocr-debug-card--fail" : ""}`}>
                  <div className="macro-ocr-debug-card-meta">
                    <span className="macro-ocr-debug-ts">video {fmtSec(row.timestamp)}</span>
                    <span className="macro-ocr-debug-clock">{gameTime}</span>
                    <span className="macro-ocr-debug-conf">conf {confLabel}</span>
                  </div>
                  {imgUrl ? (
                    <img
                      src={imgUrl}
                      alt={`${imgType} at ${row.timestamp}s`}
                      className="macro-ocr-debug-img"
                      loading="lazy"
                    />
                  ) : (
                    <div className="macro-ocr-debug-no-img">no {imgType} image</div>
                  )}
                  {row.warnings.length > 0 && (
                    <div className="macro-ocr-debug-warnings">
                      {row.warnings.map((w) => (
                        <span key={w} className="macro-ocr-debug-badge">
                          {w}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
};

export default MacroOcrDebugViewer;
